import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Users,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  UserCheck,
  UserX,
  BadgeCheck,
  AlertCircle,
  Download,
  CreditCard,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { SearchParamChanges, useSearchParamUpdater } from "../shared/hooks/useSearchParamUpdater";
import { getApiUrl } from "../config/api";
import { authFetch } from "../config/authFetch";
import { useApi } from "../context/ApiContext";
import { useProduct } from "../context/ProductContext";
import { PRODUCT_LABEL, type ProductLine } from "../config/products";
import { hasAccess, planState } from "../shared/utils/subscription";
import { downloadBlob } from "../shared/utils/download";
import {
  SIGNUP_RANGE_OPTIONS,
  isSignupRangePreset,
  signupRangeDays,
  toDay,
  type SignupRangePreset,
} from "../shared/utils/signupRange";
import {
  UserDetailDrawer,
  DetailUser,
  UserStatus,
  SubscriptionStatus,
} from "../shared/components/UserDetailDrawer";

// Signup acquisition source as returned by the backend (null = organic/direct).
type SignupSource = "google" | "facebook" | "instagram" | "meta" | null;

// Filter option unions, shared by the filter state and the query builder.
type StatusFilter = "All" | "Active" | "Inactive";
type BrandFilter = "" | "with" | "without";
type ActivityFilter = "" | "topics" | "posts" | "published" | "no_topics" | "no_posts";
type AccountTypeFilter = "" | "user" | "agency";
type SubscriptionFilter = "" | "paid" | "trial" | "free" | "past_due" | "none";
type VerifiedFilter = "" | "true" | "false";
// The count pills: the three account states plus users paying right now.
type CountView = StatusFilter | "Paid";
type PlatformFilter = "All" | "iOS" | "web";

interface User extends DetailUser {
  _id: string;
  isGuest?: boolean;
  isAgency?: boolean;
}

interface ApiUser {
  uuid: string;
  id: number;
  email: string;
  role: number;
  firstName: string;
  lastName: string;
  authProvider: number;
  profilePic: string | null;
  freeUntil: string | null;
  createdAt: string;
  isDeleted: number;
  signupPlatform?: string;
  signupSource?: SignupSource;
  country?: string;
  products?: ProductLine[];
  lastLoginAt?: string | null;
  verified?: boolean;
  subscriptions: Array<{
    provider: string;
    status: string;
    startDate: string | null;
    endDate: string | null;
  }>;
}

interface PaginationData {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface UsersApiResponse {
  status: string;
  data: {
    counts: { total: number; active: number; inactive: number; paid?: number };
    users: ApiUser[];
    pagination: PaginationData;
  };
  message?: string;
}

interface UsersQueryParams {
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  search: string;
  status: StatusFilter;
  subscription: SubscriptionFilter;
  accountType: AccountTypeFilter;
  verified: VerifiedFilter;
  platform: PlatformFilter;
  product: ProductLine;
  joinedFrom: string;
  joinedTo: string;
  brand: BrandFilter;
  activity: ActivityFilter;
}

// Avatar helpers — deterministic colour + initials per user.
const AVATAR_COLORS = [
  "bg-violet-500",
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-rose-500",
];

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  return parts
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
};

const avatarColor = (seed: string): string => {
  let hash = 0;
  for (const ch of seed) {
    hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) % 2147483647;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Build the query string for GET /users from the current filter/pagination state.
const buildUsersQuery = (params: UsersQueryParams): URLSearchParams => {
  const query = new URLSearchParams();
  query.append("page", params.page.toString());
  query.append("pageSize", params.pageSize.toString());
  query.append("sortBy", params.sortBy);
  query.append("sortOrder", params.sortOrder);

  const search = params.search.trim();
  if (search) query.append("search", search);
  if (params.status !== "All") {
    query.append("status", params.status === "Active" ? "1" : "2");
  }
  if (params.platform !== "All") query.append("platform", params.platform);
  query.append("product", params.product);
  const { joinedFrom, joinedTo, brand, activity, subscription, accountType, verified } = params;
  if (subscription) query.append("subscription", subscription);
  if (accountType) query.append("accountType", accountType);
  if (verified) query.append("verified", verified);
  // Dates are picked in the admin's local time zone; the API filters on exact instants.
  if (joinedFrom) query.append("joinedFrom", new Date(`${joinedFrom}T00:00:00`).toISOString());
  if (joinedTo) query.append("joinedTo", new Date(`${joinedTo}T23:59:59.999`).toISOString());
  if (brand) query.append("brand", brand);
  if (activity) query.append("activity", activity);

  return query;
};

// Convert a raw API user into the shape the table renders.
const mapApiUserToUser = (apiUser: ApiUser): User => {
  // Live + verified → Active; live-but-unverified → Pending; deleted → Inactive.
  let status: UserStatus;
  if (apiUser.isDeleted === 1) {
    status = apiUser.verified ? "Active" : "Pending";
  } else {
    status = "Inactive";
  }

  const states = apiUser.subscriptions.map((sub) => planState(sub));
  const isFreeUntilValid = apiUser.freeUntil
    ? new Date(apiUser.freeUntil) > new Date()
    : false;

  let subscriptionStatus: SubscriptionStatus;
  if (states.some((state) => hasAccess(state) && state !== "free")) {
    subscriptionStatus = "Active";
  } else if (states.includes("free") || isFreeUntilValid) {
    subscriptionStatus = "Free";
  } else {
    subscriptionStatus = "Inactive";
  }

  const fullName = `${apiUser.firstName || ""} ${apiUser.lastName || ""}`.trim();

  return {
    _id: apiUser.uuid || apiUser.id.toString(),
    id: apiUser.id,
    uuid: apiUser.uuid,
    name: fullName,
    email: apiUser.email || "",
    status,
    subscriptionStatus,
    createdAt: apiUser.createdAt || "",
    signupPlatform: apiUser.signupPlatform || "",
    signupSource: apiUser.signupSource ?? null,
    authProvider: apiUser.authProvider ?? null,
    country: apiUser.country || "",
    products: apiUser.products || [],
    lastLoginAt: apiUser.lastLoginAt ?? null,
    verified: apiUser.verified ?? false,
    subscriptions: apiUser.subscriptions || [],
    isGuest: apiUser.authProvider === 5,
    isAgency: apiUser.role === 3,
  };
};

// Human-friendly "time ago" for last-active. null/empty → "Never".
const timeAgo = (value?: string | null): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Status pill (Active / Inactive) with a leading dot.
const STATUS_STYLES: Record<UserStatus, { pill: string; dot: string }> = {
  Active: { pill: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  Pending: { pill: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  Inactive: { pill: "bg-gray-100 text-gray-600", dot: "bg-gray-400" },
};

const StatusBadge: React.FC<{ status: UserStatus }> = ({ status }) => {
  const style = STATUS_STYLES[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.pill}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {status}
    </span>
  );
};

// Subscription pill — colour keyed to the real status we have (Active/Free/Inactive).
const SUB_BADGE_CLASSES: Record<SubscriptionStatus, string> = {
  Active: "bg-violet-50 text-violet-700",
  Free: "bg-blue-50 text-blue-700",
  Inactive: "bg-gray-100 text-gray-500",
};

const SubscriptionBadge: React.FC<{ status: SubscriptionStatus }> = ({
  status,
}) => {
  const classes = SUB_BADGE_CLASSES[status];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${classes}`}
    >
      {status}
    </span>
  );
};

// Styled native <select> matching the toolbar look.
const FilterSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}> = ({ value, onChange, children }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-9 py-2.5 text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
    >
      {children}
    </select>
    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
  </div>
);

const PAGE_SIZES = new Set([10, 25, 50, 100]);
const DEFAULT_PAGE_SIZE = 10;

// The first option of each list is "no filter".
type Option<T extends string> = { value: T; label: string };

const STATUS_FILTER_OPTIONS: ReadonlyArray<Option<StatusFilter>> = [
  { value: "All", label: "Any status" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const SUBSCRIPTION_OPTIONS: ReadonlyArray<Option<SubscriptionFilter>> = [
  { value: "", label: "Any plan" },
  { value: "paid", label: "Paid users" },
  { value: "trial", label: "On trial" },
  { value: "free", label: "Free plan users" },
  { value: "past_due", label: "Payment due" },
  { value: "none", label: "No plan yet" },
];
const ACCOUNT_TYPE_OPTIONS: ReadonlyArray<Option<AccountTypeFilter>> = [
  { value: "", label: "Any type" },
  { value: "user", label: "Users" },
  { value: "agency", label: "Agencies" },
];
const VERIFIED_OPTIONS: ReadonlyArray<Option<VerifiedFilter>> = [
  { value: "", label: "Any" },
  { value: "true", label: "Verified" },
  { value: "false", label: "Not verified" },
];
const BRAND_FILTER_OPTIONS: ReadonlyArray<Option<BrandFilter>> = [
  { value: "", label: "Any" },
  { value: "with", label: "Has a brand" },
  { value: "without", label: "No brand yet" },
];
const ACTIVITY_OPTIONS: ReadonlyArray<Option<ActivityFilter>> = [
  { value: "", label: "Any activity" },
  { value: "topics", label: "Topics generated" },
  { value: "posts", label: "Posts generated" },
  { value: "published", label: "Posts published" },
  { value: "no_topics", label: "Brand added, no topics yet" },
  { value: "no_posts", label: "Topics generated, no posts yet" },
];
const PLATFORM_FILTER_OPTIONS: ReadonlyArray<Option<PlatformFilter>> = [
  { value: "All", label: "Any platform" },
  { value: "iOS", label: "iOS" },
  { value: "web", label: "Web" },
];


const pickValue = <T extends string>(value: string | null, options: ReadonlyArray<Option<T>>): T =>
  options.find((o) => o.value === value)?.value ?? options[0].value;

interface PanelFilterDef {
  param: string;
  label: string;
  options: ReadonlyArray<Option<string>>;
  /** Shown only for this product. */
  only?: ProductLine;
}

interface PanelFilter extends PanelFilterDef {
  value: string;
  onChange: (value: string) => void;
}

const PANEL_FILTER_DEFS: ReadonlyArray<PanelFilterDef> = [
  { param: "status", label: "Account status", options: STATUS_FILTER_OPTIONS },
  { param: "subscription", label: "Plan", options: SUBSCRIPTION_OPTIONS },
  { param: "accountType", label: "Account type", options: ACCOUNT_TYPE_OPTIONS },
  { param: "verified", label: "Email", options: VERIFIED_OPTIONS },
  { param: "brand", label: "Brand", options: BRAND_FILTER_OPTIONS, only: "DRAWPOST_ADS" },
  { param: "activity", label: "Activity", options: ACTIVITY_OPTIONS, only: "DRAWPOST_ADS" },
  { param: "platform", label: "Platform", options: PLATFORM_FILTER_OPTIONS, only: "POSTERMAKER" },
];

/** URL params the Filters panel owns, cleared together by "Clear all" (paid=true is the old Plan link). */
const PANEL_FILTER_PARAMS = [...PANEL_FILTER_DEFS.map((d) => d.param), "paid"];

// Choosing the first option removes the param; Plan also drops the old paid=true flag.
const panelFilterChanges = (def: PanelFilterDef, value: string): SearchParamChanges => ({
  [def.param]: value === def.options[0]?.value ? null : value,
  ...(def.param === "subscription" ? { paid: null } : {}),
});

const buildPanelFilters = (
  product: ProductLine,
  values: Readonly<Record<string, string>>,
  update: (changes: SearchParamChanges) => void
): PanelFilter[] =>
  PANEL_FILTER_DEFS.filter((d) => d.only === undefined || d.only === product).map((d) => ({
    ...d,
    value: values[d.param] ?? d.options[0]?.value ?? "",
    onChange: (value) => update(panelFilterChanges(d, value)),
  }));

const PanelSelect: React.FC<Readonly<{ filter: PanelFilter }>> = ({ filter }) => (
  <label className="flex flex-col gap-1.5 text-xs font-medium text-gray-500">
    <span>{filter.label}</span>
    <span className="relative">
      <select
        value={filter.value}
        onChange={(e) => filter.onChange(e.target.value)}
        className="w-full appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-9 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
      >
        {filter.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </span>
  </label>
);

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dateInputClass =
  "px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500";
const dayParam = (value: string | null) => (value && DAY.test(value) ? value : "");

const UserManagementScreen: React.FC = () => {
  const { refreshTrigger } = useApi();
  const { product } = useProduct();
  // Brands and their activity exist only on DrawPost; signup platform (iOS/web) only matters for Postermaker.
  const showDrawpostFilters = product === "DRAWPOST_ADS";
  const showPlatformFilter = product === "POSTERMAKER";

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  // Filters, search and page live in the URL so they survive opening a user and coming back, or a refresh.
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(() => PANEL_FILTER_PARAMS.some((k) => searchParams.get(k)));
  const debouncedSearchTerm = searchParams.get("q") ?? "";
  const filterStatus = pickValue(searchParams.get("status"), STATUS_FILTER_OPTIONS);
  const filterPlatform = showPlatformFilter ? pickValue(searchParams.get("platform"), PLATFORM_FILTER_OPTIONS) : "All";
  // Older links used paid=true for the Paid card.
  const filterSubscription = pickValue(
    searchParams.get("subscription") ?? (searchParams.get("paid") === "true" ? "paid" : null),
    SUBSCRIPTION_OPTIONS
  );
  const filterAccountType = pickValue(searchParams.get("accountType"), ACCOUNT_TYPE_OPTIONS);
  const filterVerified = pickValue(searchParams.get("verified"), VERIFIED_OPTIONS);
  const customFrom = dayParam(searchParams.get("joinedFrom"));
  const customTo = dayParam(searchParams.get("joinedTo"));
  // Links from before the presets carry only joinedFrom/joinedTo: treat them as a custom range.
  const rangeParam = searchParams.get("range");
  let signupRange: SignupRangePreset = customFrom || customTo ? "custom" : "all";
  if (isSignupRangePreset(rangeParam)) signupRange = rangeParam;
  const { from: joinedFrom, to: joinedTo } = signupRangeDays(signupRange, { from: customFrom, to: customTo });
  const brandFilter = showDrawpostFilters ? pickValue(searchParams.get("brand"), BRAND_FILTER_OPTIONS) : "";
  const activityFilter = showDrawpostFilters
    ? pickValue(searchParams.get("activity"), ACTIVITY_OPTIONS)
    : "";
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "", 10) || 1);
  const requestedPageSize = Number(searchParams.get("pageSize"));
  const pageSize = PAGE_SIZES.has(requestedPageSize) ? requestedPageSize : DEFAULT_PAGE_SIZE;
  const [searchTerm, setSearchTerm] = useState<string>(debouncedSearchTerm);

  const updateSearchParams = useSearchParamUpdater();
  // Any filter change goes back to page 1.
  const updateQuery = useCallback(
    (changes: SearchParamChanges, resetPage = true) =>
      updateSearchParams(resetPage ? { ...changes, page: null } : changes),
    [updateSearchParams]
  );
  const setPage = (next: number) => updateQuery({ page: next > 1 ? next : null }, false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [totalUsersCount, setTotalUsersCount] = useState<number>(0);
  const [activeUsersCount, setActiveUsersCount] = useState<number>(0);
  const [inactiveUsersCount, setInactiveUsersCount] = useState<number>(0);
  const [paidUsersCount, setPaidUsersCount] = useState<number>(0);

  const [sortBy] = useState<string>("createdAt");
  const [sortOrder] = useState<"asc" | "desc">("desc");

  const [detailUser, setDetailUser] = useState<User | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const latestRequestRef = useRef(0);

  // Switching product starts the list over; `product` is a legacy URL filter.
  const previousProductRef = useRef(product);
  useEffect(() => {
    if (previousProductRef.current === product) return;
    previousProductRef.current = product;
    updateQuery({ product: null, brand: null, activity: null, platform: null });
  }, [product, updateQuery]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchTerm.trim() === debouncedSearchTerm) return;
    searchTimeoutRef.current = setTimeout(() => {
      updateQuery({ q: searchTerm.trim() || null });
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, debouncedSearchTerm, updateQuery]);

  // Browser back/forward changes the URL; keep the search box in step.
  useEffect(() => {
    setSearchTerm((current) => (current.trim() === debouncedSearchTerm ? current : debouncedSearchTerm));
  }, [debouncedSearchTerm]);

  // Downloads every user matching the current filters, not just this page.
  const exportUsers = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const query = buildUsersQuery({
        page,
        pageSize,
        sortBy,
        sortOrder,
        search: debouncedSearchTerm,
        status: filterStatus,
        subscription: filterSubscription,
        accountType: filterAccountType,
        verified: filterVerified,
        platform: filterPlatform,
        product,
        joinedFrom,
        joinedTo,
        brand: brandFilter,
        activity: activityFilter,
      });
      query.delete("page");
      query.delete("pageSize");
      const response = await authFetch(getApiUrl(`/api/auth/v1.0.0/admin/users/export?${query.toString()}`), {
        headers: { accept: "text/csv" },
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.message || `HTTP ${response.status}`);
      }
      downloadBlob(await response.blob(), `users-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const serialOf = (index: number) => (page - 1) * pageSize + index + 1;

  const fetchUsers = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    const isStale = () => requestId !== latestRequestRef.current;
    try {
      setLoading(true);
      const queryParams = buildUsersQuery({
        page,
        pageSize,
        sortBy,
        sortOrder,
        search: debouncedSearchTerm,
        status: filterStatus,
        subscription: filterSubscription,
        accountType: filterAccountType,
        verified: filterVerified,
        platform: filterPlatform,
        product,
        joinedFrom,
        joinedTo,
        brand: brandFilter,
        activity: activityFilter,
      });

      const url = getApiUrl(`/api/auth/v1.0.0/users?${queryParams.toString()}`);
      const response = await authFetch(url, {
        method: "GET",
        headers: { accept: "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result: UsersApiResponse = await response.json();
      if (isStale()) return;
      if (
        result.status === "success" &&
        result.data?.users &&
        Array.isArray(result.data.users)
      ) {
        if (result.data.pagination) {
          setTotalCount(result.data.pagination.totalCount);
          setTotalPages(result.data.pagination.totalPages);
        }
        if (result.data.counts) {
          setTotalUsersCount(result.data.counts.total);
          setActiveUsersCount(result.data.counts.active);
          setInactiveUsersCount(result.data.counts.inactive);
          setPaidUsersCount(result.data.counts.paid ?? 0);
        }
        setUsers(result.data.users.map(mapApiUserToUser));
      } else {
        setUsers([]);
        setTotalCount(0);
        setTotalPages(0);
        setTotalUsersCount(0);
        setActiveUsersCount(0);
        setInactiveUsersCount(0);
        setPaidUsersCount(0);
      }
    } catch (error) {
      if (isStale()) return;
      console.error("Error fetching users:", error);
      setUsers([]);
      setTotalCount(0);
      setTotalPages(0);
      setTotalUsersCount(0);
      setActiveUsersCount(0);
      setInactiveUsersCount(0);
      setPaidUsersCount(0);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [
    page,
    pageSize,
    sortBy,
    sortOrder,
    debouncedSearchTerm,
    filterStatus,
    filterSubscription,
    filterAccountType,
    filterVerified,
    filterPlatform,
    product,
    joinedFrom,
    joinedTo,
    brandFilter,
    activityFilter,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [refreshTrigger, fetchUsers]);

  // Status and Paid are separate views of the list, so picking one clears the other.
  // The Paid card is the "Paid" plan filter; the other cards are account status, which clears it.
  const applyStatusFilter = (status: StatusFilter) => {
    const clearPaid = filterSubscription === "paid" ? { subscription: null } : {};
    updateQuery({ status: status === "All" ? null : status, paid: null, ...clearPaid });
  };
  const applyCountView = (view: CountView) => {
    if (view === "Paid") updateQuery({ status: null, paid: null, subscription: "paid" });
    else applyStatusFilter(view);
  };
  const activeCountView: CountView = filterSubscription === "paid" ? "Paid" : filterStatus;

  const panelFilters = buildPanelFilters(
    product,
    {
      status: filterStatus,
      subscription: filterSubscription,
      accountType: filterAccountType,
      verified: filterVerified,
      brand: brandFilter,
      activity: activityFilter,
      platform: filterPlatform,
    },
    updateQuery
  );
  const activeFilters = panelFilters.filter((f) => f.value !== f.options[0]?.value);
  const clearPanelFilters = () => updateQuery(Object.fromEntries(PANEL_FILTER_PARAMS.map((k) => [k, null])));

  const applySignupRange = (next: SignupRangePreset) => {
    // Custom starts from the dates currently shown, so it can be adjusted rather than retyped.
    const keepDays = next === "custom";
    updateQuery({
      range: next === "all" ? null : next,
      joinedFrom: keepDays ? joinedFrom || null : null,
      joinedTo: keepDays ? joinedTo || null : null,
    });
  };

  // Count pills: for the selected product and signup range; search, platform and brand don't change them.
  const statPills: Array<{
    key: CountView;
    label: string;
    value: number;
    icon: React.ReactNode;
  }> = [
    {
      key: "All",
      label: "Total Users",
      value: totalUsersCount,
      icon: <Users className="w-4 h-4" />,
    },
    {
      key: "Active",
      label: "Active",
      value: activeUsersCount,
      icon: <UserCheck className="w-4 h-4" />,
    },
    {
      key: "Inactive",
      label: "Inactive",
      value: inactiveUsersCount,
      icon: <UserX className="w-4 h-4" />,
    },
    {
      key: "Paid",
      label: "Paid",
      value: paidUsersCount,
      icon: <CreditCard className="w-4 h-4" />,
    },
  ];

  const today = toDay(new Date());
  const firstRow = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, totalCount);

  return (
    <div className="min-h-full bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage {PRODUCT_LABEL[product]} users, subscriptions and activity.
        </p>
      </div>

      {/* Stat pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        {statPills.map((pill) => {
          const active = activeCountView === pill.key;
          return (
            <button
              key={pill.key}
              onClick={() => applyCountView(pill.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                active
                  ? "bg-violet-600 border-violet-600 text-white shadow-sm"
                  : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
              }`}
            >
              {pill.icon}
              <span>{pill.label}</span>
              <span
                className={`px-1.5 py-0.5 rounded-md text-xs font-semibold ${
                  active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                }`}
              >
                {pill.value.toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 mb-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect value={signupRange} onChange={(v) => applySignupRange(v as SignupRangePreset)}>
              {SIGNUP_RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.value === "all" ? "Signed up: all time" : o.label}
                </option>
              ))}
            </FilterSelect>

            <button
              type="button"
              onClick={() => setShowFilters((open) => !open)}
              aria-expanded={showFilters}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                activeFilters.length > 0
                  ? "border-violet-300 bg-violet-50 text-violet-700"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilters.length > 0 && (
                <span className="rounded-full bg-violet-600 px-1.5 text-xs font-semibold text-white">{activeFilters.length}</span>
              )}
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>

            <button
              type="button"
              onClick={exportUsers}
              disabled={exporting || totalCount === 0}
              title="Download all users matching these filters"
              className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exporting ? "Exporting…" : `Export CSV (${totalCount.toLocaleString()})`}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-3 grid grid-cols-1 gap-3 border-t border-gray-100 pt-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {panelFilters.map((f) => (
              <PanelSelect key={f.param} filter={f} />
            ))}
          </div>
        )}

        {activeFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {activeFilters.map((f) => (
              <span
                key={f.param}
                className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 py-1 pl-3 pr-1.5 text-xs font-medium text-violet-700"
              >
                {f.label}: {f.options.find((o) => o.value === f.value)?.label}
                <button
                  type="button"
                  onClick={() => f.onChange(f.options[0]?.value ?? "")}
                  aria-label={`Remove ${f.label} filter`}
                  className="rounded-full p-0.5 hover:bg-violet-100"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
            <button type="button" onClick={clearPanelFilters} className="text-xs font-medium text-gray-500 hover:text-gray-800">
              Clear all
            </button>
          </div>
        )}
        {signupRange === "custom" && (
          <div className="mt-3 flex flex-col gap-3 border-t border-gray-100 pt-3 sm:flex-row sm:items-end">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
              <span>Signed up from</span>
              <input
                type="date"
                value={customFrom}
                max={customTo || today}
                onChange={(e) => updateQuery({ joinedFrom: e.target.value || null })}
                className={dateInputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
              <span>Signed up to</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                max={today}
                onChange={(e) => updateQuery({ joinedTo: e.target.value || null })}
                className={dateInputClass}
              />
            </label>
          </div>
        )}
        {exportError && (
          <p className="mt-2 flex items-center gap-1.5 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" /> Could not export users: {exportError}
          </p>
        )}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-violet-600 animate-spin mb-3" />
            <p className="text-sm text-gray-500">Loading users...</p>
          </div>
        )}
        {!loading && users.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              No users found
            </h3>
            <p className="text-sm text-gray-500">
              {searchTerm ? `No users match "${searchTerm}"` : "No users available"}
            </p>
          </div>
        )}
        {!loading && users.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full" style={{ minWidth: "1000px" }}>
                <thead>
                  <tr className="border-b border-gray-200">
                    {[
                      "#",
                      "User",
                      "Product",
                      "Status",
                      "Subscription",
                      "Last Active",
                      "Registered",
                      "",
                    ].map((h, i, arr) => (
                      <th
                        key={h || `actions-${i}`}
                        className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
                          i === arr.length - 1 ? "text-right" : "text-left"
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr
                      key={user._id}
                      onClick={() => setDetailUser(user)}
                      className={`border-b border-gray-100 last:border-0 cursor-pointer transition-colors ${
                        detailUser?._id === user._id
                          ? "bg-violet-50"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-5 py-3.5 text-xs text-gray-400 tabular-nums">
                        {serialOf(index)}
                      </td>
                      {/* User */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3 max-w-[280px]">
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 ${avatarColor(
                              user.name || user.email
                            )}`}
                          >
                            {initialsOf(user.name || user.email)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span
                                className="text-sm font-medium text-gray-900 truncate"
                                title={user.name}
                              >
                                {user.name || "Unnamed"}
                              </span>
                              {user.isGuest && (
                                <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">
                                  Guest
                                </span>
                              )}
                              {user.isAgency && (
                                <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
                                  Agency
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <p
                                className="text-xs text-gray-500 truncate"
                                title={user.email}
                              >
                                {user.email}
                              </p>
                              {user.verified ? (
                                <BadgeCheck
                                  className="w-3.5 h-3.5 text-emerald-500 shrink-0"
                                  aria-label="Email verified"
                                />
                              ) : (
                                <AlertCircle
                                  className="w-3.5 h-3.5 text-amber-500 shrink-0"
                                  aria-label="Email not verified"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Product */}
                      <td className="px-5 py-3.5">
                        {user.products.length === 0 ? (
                          <span className="text-gray-400 text-sm">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {user.products.map((p) => (
                              <span
                                key={p}
                                className="inline-flex items-center gap-1.5 text-sm text-gray-700"
                              >
                                <span className="w-5 h-5 rounded bg-violet-100 flex items-center justify-center">
                                  <span className="w-2 h-2 rounded-sm bg-violet-500" />
                                </span>
                                {PRODUCT_LABEL[p] ?? p}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-3.5">
                        <StatusBadge status={user.status} />
                      </td>

                      {/* Subscription */}
                      <td className="px-5 py-3.5">
                        <SubscriptionBadge status={user.subscriptionStatus} />
                      </td>

                      {/* Last Active */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              user.lastLoginAt ? "bg-emerald-500" : "bg-gray-300"
                            }`}
                          />
                          {timeAgo(user.lastLoginAt)}
                        </span>
                      </td>

                      {/* Registered */}
                      <td className="px-5 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(user.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailUser(user);
                          }}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                          title="View details"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-gray-100">
              {users.map((user, index) => (
                <button
                  key={user._id}
                  onClick={() => setDetailUser(user)}
                  className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 shrink-0 text-xs text-gray-400 tabular-nums">
                      {serialOf(index)}
                    </span>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0 ${avatarColor(
                        user.name || user.email
                      )}`}
                    >
                      {initialsOf(user.name || user.email)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.name || "Unnamed"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user.email}
                      </p>
                    </div>
                    <StatusBadge status={user.status} />
                  </div>
                  <div className="flex items-center gap-2 mt-3 pl-[88px]">
                    <SubscriptionBadge status={user.subscriptionStatus} />
                    <span className="text-xs text-gray-400">
                      {formatDate(user.createdAt)}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Pagination */}
            <div className="border-t border-gray-200 px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>
                  Showing {firstRow}–{lastRow} of {totalCount.toLocaleString()}{" "}
                  users
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const size = Number(e.target.value);
                    updateQuery({ pageSize: size === DEFAULT_PAGE_SIZE ? null : size });
                  }}
                  className="ml-2 px-2 py-1 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1 || loading}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (page <= 3) pageNum = i + 1;
                  else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = page - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      disabled={loading}
                      className={`min-w-[36px] h-9 px-2 text-sm font-medium rounded-lg ${
                        page === pageNum
                          ? "bg-violet-600 text-white"
                          : "text-gray-600 border border-gray-200 hover:bg-gray-50"
                      } disabled:opacity-40`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || totalPages === 0 || loading}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <UserDetailDrawer
        isOpen={detailUser !== null}
        user={detailUser}
        onClose={() => setDetailUser(null)}
      />
    </div>
  );
};

export default UserManagementScreen;
